import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getIntegrations,
  getIntegration,
  createIntegration,
  updateIntegration,
  deleteIntegration,
  testIntegration,
  triggerSync,
} from '../api/integrations';

/** `enabled: false` for anyone without the Integrations area — the API answers them 403. */
export function useIntegrations(enabled = true) {
  const queryClient = useQueryClient();
  const hadRunningRef = useRef(false);

  const query = useQuery({
    queryKey: ['integrations'],
    queryFn: getIntegrations,
    enabled,
    refetchInterval: (q) => {
      // Poll only while a sync is genuinely running; a "running" row older than an
      // hour is stuck (the API reports it as such) and would otherwise poll forever.
      const hasRunning = q.state.data?.integrations.some(
        (i) =>
          i.lastSyncStatus === 'running' &&
          !!i.lastSyncAt &&
          Date.now() - new Date(i.lastSyncAt).getTime() < 60 * 60 * 1000
      );
      return hasRunning ? 5_000 : false;
    },
  });

  // When a sync transitions from running → done, invalidate dashboard queries
  // so MRR overview picks up the new subscription data immediately.
  const hasRunning = query.data?.integrations.some((i) => i.lastSyncStatus === 'running') ?? false;

  useEffect(() => {
    if (hadRunningRef.current && !hasRunning) {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    }
    hadRunningRef.current = hasRunning;
  }, [hasRunning, queryClient]);

  return query;
}

export function useIntegration(id: string) {
  return useQuery({
    queryKey: ['integrations', id],
    queryFn: () => getIntegration(id),
    enabled: !!id,
  });
}

export function useCreateIntegration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createIntegration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });
}

export function useUpdateIntegration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof updateIntegration>[1] }) =>
      updateIntegration(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      queryClient.invalidateQueries({ queryKey: ['integrations', id] });
    },
  });
}

export function useDeleteIntegration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteIntegration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });
}

export function useTestIntegration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: testIntegration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });
}

export function useTriggerSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, options }: { id: string; options?: Parameters<typeof triggerSync>[1] }) =>
      triggerSync(id, options),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      queryClient.invalidateQueries({ queryKey: ['integrations', id] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}
